import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PrimaryButton, SecondaryButton, Field, inputCls } from "@/components/ui-kit";
import { X, Send, FileText, Paperclip, Bookmark, Tag } from "lucide-react";
import { toast } from "sonner";
import { TEMPLATE_CATEGORIES } from "@/components/EmailTemplatesSection";

const TEMPLATE_OPTIONS = [
  // Todas las plantillas están SIEMPRE disponibles cuando se abre desde un servicio.
  // Si el servicio no tiene datos para "Cotización" o "Informe + Cotización",
  // avisamos con un banner (ver `quoteDataMissing`) en vez de esconder la opción.
  { key: "report", label: "Informe de mantención", needs: ["service_id"] },
  { key: "quote", label: "Cotización", needs: [] },
  { key: "report_and_quote", label: "Informe + Cotización", needs: ["service_id"] },
  { key: "reminder", label: "Recordatorio de pago", needs: ["payment_id"] },
  { key: "custom", label: "Personalizado", needs: [] },
];

/**
 * Reusable modal to compose and send emails via the branded templates.
 * Pass the context via props: serviceId, quoteId, paymentId, customerId,
 * plus `allowedTemplates` (array of keys) to restrict which options appear.
 * `hasQuotableRepairs` (bool): si el servicio trae repair_items con precio > 0,
 * habilita "Informe + Cotización" aunque no exista quoteId formal (se sintetiza en el backend).
 */
export default function EmailComposeModal({
  onClose,
  serviceId,
  quoteId,
  paymentId,
  customerId,
  reportVersion,
  hasQuotableRepairs = false,
  defaultTemplate = "custom",
  allowedTemplates,
  onSent,
}) {
  const [template, setTemplate] = useState(defaultTemplate);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [attachReport, setAttachReport] = useState(!!serviceId);
  const [attachQuote, setAttachQuote] = useState(!!quoteId || !!hasQuotableRepairs);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [invoiceFiles, setInvoiceFiles] = useState([]); // [{filename, data (base64), mime, size}]
  const [officeReportVersion, setOfficeReportVersion] = useState(reportVersion || null);
  useEffect(() => {
    if (!serviceId || reportVersion) return;
    api.get(`/services/${serviceId}`).then(({ data }) => setOfficeReportVersion(data.office_report?.version || null)).catch(() => {});
  }, [serviceId, reportVersion]);
  const sendAttempt = useRef({ key: crypto.randomUUID(), fingerprint: null });
  const sendLock = useRef(false);
  const [sendError, setSendError] = useState("");
  const [googleConnected, setGoogleConnected] = useState(null);
  const [googleCheckError, setGoogleCheckError] = useState(false);
  const googleRequest = useRef(0);
  const checkGoogle = useCallback(async () => {
    const ticket = ++googleRequest.current;
    setGoogleConnected(null); setGoogleCheckError(false);
    try {
      const { data } = await api.get("/integrations/google/status", { timeout: 15000 });
      if (ticket === googleRequest.current) {
        if (typeof data.connected !== "boolean") throw new Error("Invalid Google connection response");
        setGoogleConnected(data.connected);
      }
    } catch {
      if (ticket === googleRequest.current) setGoogleCheckError(true);
    }
  }, []);
  useEffect(() => { checkGoogle(); return () => { googleRequest.current += 1; }; }, [checkGoogle]);

  // Fetch saved custom templates for the picker
  useEffect(() => {
    api.get("/email-templates").then(({ data }) => setSavedTemplates(data)).catch(() => setSavedTemplates([]));
  }, []);

  const applySavedTemplate = (tpl) => {
    setTemplate("custom");
    setCustomSubject(tpl.subject || "");
    setCustomBody(tpl.body || "");
    toast.success(`Plantilla "${tpl.name}" aplicada`);
  };

  const readAsBase64 = (file) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => {
        const s = String(r.result || "");
        // Devuelve base64 puro (sin prefix data:*)
        res(s.includes(",") ? s.split(",", 2)[1] : s);
      };
      r.onerror = rej;
      r.readAsDataURL(file);
    });
  const handleInvoiceFiles = async (files) => {
    if (!files?.length) return;
    const list = Array.from(files);
    if (list.reduce((sum, f) => sum + f.size, invoiceFiles.reduce((sum, f) => sum + f.size, 0)) > 18 * 1024 * 1024) { toast.error("Los adjuntos adicionales no pueden superar 18 MB en total"); return; }
    if (list.some((f) => !f.name.toLowerCase().endsWith(".pdf"))) { toast.error("Adjunta documentos en formato PDF"); return; }
    const encoded = await Promise.all(list.map(async (f) => ({
      filename: f.name,
      data: await readAsBase64(f),
      mime: f.type || "application/pdf",
      size: f.size,
    })));
    setInvoiceFiles((prev) => [...prev, ...encoded]);
  };
  const removeInvoiceFile = (idx) => setInvoiceFiles((prev) => prev.filter((_, i) => i !== idx));
  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  const contextHas = (n) => {
    if (n === "service_id") return !!serviceId;
    if (n === "quote_id") return !!quoteId;
    if (n === "payment_id") return !!paymentId;
    if (n === "has_quotable_repairs") return !!hasQuotableRepairs;
    return false;
  };
  const options = TEMPLATE_OPTIONS.filter((t) => {
    if (allowedTemplates && !allowedTemplates.includes(t.key)) return false;
    for (const n of (t.needs || [])) {
      if (!contextHas(n)) return false;
    }
    if (t.needsAny && t.needsAny.length && !t.needsAny.some(contextHas)) return false;
    return true;
  });

  // Si el usuario selecciona una plantilla que necesita cotización pero el servicio
  // no tiene ni un quote formal ni repair_items con precio, avisamos con un banner.
  const quoteDataMissing = (template === "quote" || template === "report_and_quote")
    && !quoteId
    && !hasQuotableRepairs;

  // Nombres de archivos que realmente se adjuntarán — para que el body los enumere.
  const attachmentNames = useMemo(() => {
    const names = [];
    const wantsReport = !!attachReport && !!serviceId;
    const wantsQuote = !!attachQuote && (!!quoteId || !!hasQuotableRepairs);
    if (wantsReport && serviceId) names.push("Informe de mantención (PDF)");
    if (wantsQuote) names.push("Cotización (PDF)");
    for (const f of invoiceFiles) names.push(f.filename || "Documento adjunto");
    return names;
  }, [attachReport, attachQuote, serviceId, quoteId, hasQuotableRepairs, invoiceFiles]);

  const doPreview = async (tpl = template) => {
    setLoading(true);
    try {
      const { data } = await api.post("/emails/preview", {
        template: tpl,
        service_id: serviceId || null,
        quote_id: quoteId || null,
        payment_id: paymentId || null,
        customer_id: customerId || null,
        extra_note: extraNote,
        custom_subject: customSubject,
        custom_body: customBody,
        attachment_names: attachmentNames,
        attach_report: !!attachReport && !!serviceId,
        attach_quote: !!attachQuote && (!!quoteId || !!hasQuotableRepairs),
      });
      if (!to) setTo(data.to || "");
      setSubject(data.subject || "");
      setBodyHtml(data.body_html || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // On first mount / template change, fetch a fresh preview from backend.
  useEffect(() => { doPreview(template); /* eslint-disable-next-line */ }, [template]);
  // Re-preview cuando cambian los adjuntos para que el body refleje los archivos correctos.
  useEffect(() => { doPreview(template); /* eslint-disable-next-line */ }, [attachReport, attachQuote, invoiceFiles.length]);
  // Debounce re-preview when extraNote/custom changes for custom template
  useEffect(() => {
    if (template !== "custom") return;
    const t = setTimeout(() => doPreview("custom"), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [customSubject, customBody]);
  useEffect(() => {
    const t = setTimeout(() => { if (template !== "custom") doPreview(template); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [extraNote]);

  const send = async () => {
    if (!to || !subject) { toast.error("Falta destinatario o asunto"); return; }
    if (sendLock.current) return;
    if (googleConnected !== true) { toast.error(googleConnected === false ? "Conecta tu cuenta Google en Ajustes antes de enviar" : "Espera la comprobación de tu conexión Google antes de enviar"); return; }
    sendLock.current = true; setSending(true); setSendError("");
    try {
      const payload = {
        template,
        service_id: serviceId || null,
        quote_id: quoteId || null,
        payment_id: paymentId || null,
        customer_id: customerId || null,
        extra_note: extraNote,
        custom_subject: customSubject,
        custom_body: customBody,
        to, subject, body_html: bodyHtml,
        cc: cc || "",
        bcc: bcc || "",
        attach_report: !!attachReport && !!serviceId,
        attach_quote: !!attachQuote && (!!quoteId || !!hasQuotableRepairs),
        extra_attachments: invoiceFiles.map(({ filename, data, mime }) => ({ filename, data, mime })),
        report_version: officeReportVersion,
        attachment_names: attachmentNames,
      };
      const fingerprint = JSON.stringify(payload);
      if (sendAttempt.current.fingerprint && sendAttempt.current.fingerprint !== fingerprint) sendAttempt.current.key = crypto.randomUUID();
      sendAttempt.current.fingerprint = fingerprint;
      const { data } = await api.post("/emails/send-template", { ...payload, send_key: sendAttempt.current.key });
      toast.success(`Correo enviado (${data.attachments || 0} adjunto/s)`);
      onSent && onSent();
      onClose();
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || "No se confirmó el envío. Revisa el historial antes de volver a intentarlo.";
      setSendError(message); toast.error(message);
    } finally {
      sendLock.current = false; setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="email-compose-modal" onClick={() => !sending && onClose()}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-4xl border-t-4 border-[#FF5A00] max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Enviar correo</div>
            <div className="font-display font-bold text-xl">Redactar mensaje al cliente</div>
          </div>
          <button data-testid="email-compose-close" disabled={sending} onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {googleConnected === null && !googleCheckError && <p data-testid="email-google-checking" role="status" className="text-sm text-neutral-600">Comprobando conexión con Google…</p>}
          {googleCheckError && <div data-testid="email-google-status-error" role="alert" className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm">No se pudo comprobar la conexión de Google. <button data-testid="email-google-retry" type="button" className="underline" onClick={checkGoogle}>Reintentar comprobación</button></div>}
          {googleConnected === false && <div data-testid="email-google-not-connected" role="status" className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm">Tu cuenta de Google no está conectada. <Link data-testid="email-connect-google-link" className="underline" to="/admin/ajustes" onClick={onClose}>Ir a Ajustes</Link></div>}
          {sendError && <div data-testid="email-send-error" role="alert" className="border-l-4 border-red-500 bg-red-50 p-3 text-sm">{sendError}{serviceId && <Link data-testid="email-send-history-link" className="block underline mt-2" to={`/admin/revision-visitas/${serviceId}`} onClick={onClose}>Volver a la visita y consultar el historial</Link>}</div>}
          <Field label="Plantilla">
            <div className="flex flex-wrap gap-2" data-testid="email-templates">
              {options.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTemplate(t.key)}
                  data-testid={`email-template-${t.key}`}
                  className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border ${template === t.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-neutral-500 border-neutral-300 hover:border-[#FF5A00]"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          {quoteDataMissing && (
            <div
              data-testid="email-quote-missing-warning"
              className="border border-[#FFC107]/50 bg-[#FFF8E1] px-3 py-2 text-[11px] text-[#8A6600] leading-snug"
            >
              <div className="font-mono-kpi text-[10px] uppercase tracking-widest text-[#B28900] mb-0.5">
                ⚠ Este servicio no tiene cotización lista
              </div>
              No hay reparaciones con precio ni una cotización formal ligada. Puedes:
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Agregar ítems con precio en <b>Reparaciones detectadas</b> antes de enviar.</li>
                <li>Adjuntar la cotización como PDF manual desde <b>Adjuntar factura</b> más abajo.</li>
                <li>O elegir la plantilla <b>Personalizado</b> si sólo quieres redactar un mensaje.</li>
              </ul>
            </div>
          )}

          {template === "custom" ? (
            <>
              {savedTemplates.length > 0 && (
                <Field label="Cargar desde plantilla guardada">
                  <div className="flex flex-wrap gap-1.5" data-testid="email-saved-templates">
                    {savedTemplates.map((t) => {
                      const cat = TEMPLATE_CATEGORIES.find((c) => c.id === (t.category || "otro")) || TEMPLATE_CATEGORIES[3];
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => applySavedTemplate(t)}
                          data-testid={`email-saved-template-${t.id}`}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 font-mono-kpi text-[10px] uppercase tracking-[0.1em] border hover:border-[#FF5A00] hover:text-[#FF5A00] ${cat.cls}`}
                          title={t.subject}
                        >
                          <Tag className="w-3 h-3" />{t.name}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}
              <Field label="Asunto *">
                <input value={customSubject} onChange={(e) => setCustomSubject(e.target.value)} className={inputCls} data-testid="email-custom-subject" placeholder="Asunto del correo" />
              </Field>
              <Field label="Cuerpo (párrafos separados por doble salto de línea)">
                <textarea rows={6} value={customBody} onChange={(e) => setCustomBody(e.target.value)} className={inputCls} data-testid="email-custom-body"
                  placeholder="Estimado(a),&#10;&#10;Le escribo para..." />
              </Field>
            </>
          ) : (
            <Field label="Nota adicional (opcional, se agrega como recuadro destacado)">
              <textarea rows={2} value={extraNote} onChange={(e) => setExtraNote(e.target.value)} className={inputCls}
                data-testid="email-extra-note"
                placeholder="Ej: Solicitamos aprobar antes del viernes para agendar el trabajo la próxima semana." />
            </Field>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={
              <div className="flex items-center justify-between">
                <span>Destinatario *</span>
                <button
                  type="button"
                  onClick={() => setShowCcBcc((v) => !v)}
                  data-testid="email-toggle-cc-bcc"
                  className="text-[10px] font-mono-kpi uppercase tracking-wider text-[#FF5A00] hover:underline"
                >
                  {showCcBcc ? "Ocultar CC/CCO" : "+ CC / CCO"}
                </button>
              </div>
            }>
              <input type="email" required value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="email-to" placeholder="cliente@correo.cl" />
            </Field>
            <Field label="Asunto (editable) *">
              <input required value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="email-subject" />
            </Field>
          </div>

          {showCcBcc && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="CC (con copia) — separa varios con coma">
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  className={inputCls}
                  data-testid="email-cc"
                  placeholder="otro@correo.cl, tercero@correo.cl"
                />
              </Field>
              <Field label="CCO (copia oculta) — separa varios con coma">
                <input
                  type="text"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  className={inputCls}
                  data-testid="email-bcc"
                  placeholder="admin@allactive.cl"
                />
              </Field>
            </div>
          )}

          <div className="flex items-center gap-4 text-sm flex-wrap">
            {serviceId && (
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="email-attach-report">
                <input type="checkbox" checked={attachReport} onChange={(e) => setAttachReport(e.target.checked)} />
                <FileText className="w-4 h-4 text-[#FF5A00]" /> Adjuntar informe PDF
              </label>
            )}
            {(quoteId || hasQuotableRepairs) && (
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="email-attach-quote">
                <input type="checkbox" checked={attachQuote} onChange={(e) => setAttachQuote(e.target.checked)} />
                <Paperclip className="w-4 h-4 text-[#FF5A00]" /> Adjuntar cotización PDF
              </label>
            )}
          </div>

          {/* Adjuntar factura: siempre visible antes de enviar */}
          <div className="border border-neutral-200 bg-neutral-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-600">
                <Paperclip className="w-3.5 h-3.5 text-[#FF5A00]" />
                Adjuntar factura ({invoiceFiles.length})
              </div>
              <label className="cursor-pointer inline-flex items-center gap-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] hover:text-[#E04A00]" data-testid="email-attach-invoice-label">
                + Elegir archivo
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  className="hidden"
                  data-testid="email-attach-invoice-input"
                  onChange={(e) => handleInvoiceFiles(e.target.files)}
                />
              </label>
            </div>
            {invoiceFiles.length === 0 ? (
              <div className="text-[11px] text-neutral-500">
                Adjunta la factura del SII (u otro documento) para que se envíe junto con el correo. PDF o imagen.
              </div>
            ) : (
              <ul className="space-y-1" data-testid="email-attach-invoice-list">
                {invoiceFiles.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs bg-white border border-neutral-200 px-2 py-1.5" data-testid={`email-attach-invoice-item-${i}`}>
                    <div className="min-w-0 flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                      <span className="truncate">{f.filename}</span>
                      <span className="text-neutral-400 font-mono-kpi flex-shrink-0">{fmtSize(f.size)}</span>
                    </div>
                    <button type="button" onClick={() => removeInvoiceFile(i)} data-testid={`email-attach-invoice-remove-${i}`} className="text-[#B71C1C] hover:bg-[#B71C1C]/5 px-2 py-0.5 font-mono-kpi text-[10px] uppercase tracking-wider">
                      Quitar
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">Vista previa del correo</div>
              <button data-testid="email-toggle-preview" type="button" onClick={() => setShowPreview((p) => !p)} className="text-[10px] font-mono-kpi uppercase tracking-wider text-[#FF5A00] hover:underline">
                {showPreview ? "Ocultar" : "Mostrar"}
              </button>
            </div>
            {showPreview && (
              <div className="border border-neutral-200 bg-neutral-50 max-h-[420px] overflow-auto">
                <iframe title="preview" sandbox="" srcDoc={bodyHtml} className="w-full h-[420px] bg-white" data-testid="email-preview-frame" />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <div data-testid="email-send-status" className="text-[10px] font-mono-kpi uppercase tracking-wider text-neutral-500">
            {loading ? "Actualizando vista previa…" : "Listo para enviar"}
          </div>
          <div className="flex items-center gap-2">
            <SecondaryButton data-testid="email-cancel" disabled={sending} onClick={onClose}>Cancelar</SecondaryButton>
            <PrimaryButton onClick={send} disabled={sending || loading || !to || !subject || googleConnected !== true} data-testid="email-send-btn">
              <Send className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />
              {sending ? "Enviando…" : "Enviar correo"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
